import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * CommandPalette — ⌘K searchable action launcher.
 * Receives an array of action objects and renders a modal palette.
 */
export default function CommandPalette({ actions, open, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Group actions by category
  const grouped = useMemo(() => {
    const q = query.toLowerCase().trim();
    const filtered = q
      ? actions.filter(a =>
          a.label.toLowerCase().includes(q) ||
          a.description?.toLowerCase().includes(q) ||
          a.category?.toLowerCase().includes(q) ||
          a.keywords?.some(k => k.toLowerCase().includes(q))
        )
      : actions;

    const groups = {};
    for (const action of filtered) {
      const cat = action.category || 'Actions';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(action);
    }
    return groups;
  }, [actions, query]);

  const flatList = useMemo(() => {
    const items = [];
    for (const [cat, acts] of Object.entries(grouped)) {
      items.push({ type: 'header', label: cat });
      for (const a of acts) items.push({ type: 'action', ...a });
    }
    return items;
  }, [grouped]);

  const actionItems = useMemo(() => flatList.filter(i => i.type === 'action'), [flatList]);

  // Clamp selection
  useEffect(() => {
    if (selectedIdx >= actionItems.length) setSelectedIdx(Math.max(0, actionItems.length - 1));
  }, [actionItems.length, selectedIdx]);

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector('.cmd-item.selected');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const handleKey = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, actionItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = actionItems[selectedIdx];
      if (item && !item.disabled) {
        item.onAction();
        onClose();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [actionItems, selectedIdx, onClose]);

  if (!open) return null;

  let actionIdx = -1;

  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-palette" onClick={e => e.stopPropagation()}>
        <div className="cmd-search">
          <span className="cmd-search-icon">⌘</span>
          <input
            ref={inputRef}
            type="text"
            className="cmd-input"
            placeholder="Type a command..."
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIdx(0); }}
            onKeyDown={handleKey}
          />
          <kbd className="cmd-kbd">ESC</kbd>
        </div>
        <div className="cmd-list" ref={listRef}>
          {flatList.length === 0 && (
            <div className="cmd-empty">No matching actions</div>
          )}
          {flatList.map((item, i) => {
            if (item.type === 'header') {
              return <div key={`h-${item.label}`} className="cmd-group-header">{item.label}</div>;
            }
            actionIdx++;
            const isSelected = actionIdx === selectedIdx;
            const thisIdx = actionIdx;
            return (
              <button
                key={item.id || `a-${i}`}
                className={`cmd-item${isSelected ? ' selected' : ''}${item.disabled ? ' disabled' : ''}${item.active ? ' active' : ''}`}
                onClick={() => {
                  if (!item.disabled) { item.onAction(); onClose(); }
                }}
                onMouseEnter={() => setSelectedIdx(thisIdx)}
              >
                <span className="cmd-item-icon">{item.icon}</span>
                <div className="cmd-item-text">
                  <span className="cmd-item-label">{item.label}</span>
                  {item.description && <span className="cmd-item-desc">{item.description}</span>}
                </div>
                {item.shortcut && <kbd className="cmd-item-shortcut">{item.shortcut}</kbd>}
                {item.active && <span className="cmd-item-active-dot" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
